import { Check, ChevronsUpDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { WorkspaceIcon } from '@shared/components/branding/WorkspaceIcon';
import {
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@shared/components/ui/DropdownMenu';
import { cn } from '@shared/components/ui/cn';
import { useWorkspaces, type Workspace } from '@shared/hooks/use-workspaces';
import { useToastStore } from '@shared/stores/toast.store';

function WorkspaceIdentity({
  workspace,
  fallback,
}: {
  workspace: Workspace | null;
  fallback: string;
}) {
  const { t } = useTranslation();

  return (
    <div className="min-w-0 flex-1 text-start">
      <p className="truncate text-sm font-semibold leading-tight text-sidebar-foreground">
        {workspace?.name ?? fallback}
      </p>
      <p className="truncate text-xs text-muted-foreground">{t('sidebar.crm', 'CRM')}</p>
    </div>
  );
}

export function WorkspaceSwitcher() {
  const { t } = useTranslation();
  const addToast = useToastStore((s) => s.addToast);
  const { active, workspaces, canSwitch, switching, switchTo } = useWorkspaces();

  const noCompany = t('sidebar.noCompany', 'No company');

  if (!canSwitch) {
    return (
      <div className="flex items-center gap-2 rounded-md p-2" data-testid="workspace-identity">
        <WorkspaceIcon workspace={active} />
        <WorkspaceIdentity workspace={active} fallback={noCompany} />
      </div>
    );
  }

  const onSelect = (workspace: Workspace) => {
    if (workspace.accountId === active?.accountId) return;

    void switchTo(workspace.accountId).then((ok) => {
      if (!ok) {
        addToast({
          title: t('sidebar.switchWorkspaceFailed', 'Could not switch workspace'),
          variant: 'error',
        });
      }
    });
  };

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger
        hideChevron
        className="gap-2 p-2"
        data-testid="workspace-switcher-trigger"
      >
        <WorkspaceIcon workspace={active} />
        <WorkspaceIdentity workspace={active} fallback={noCompany} />
        <ChevronsUpDown className="shrink-0 text-muted-foreground" />
      </DropdownMenuSubTrigger>

      <DropdownMenuSubContent
        className="w-56 rounded-xl p-1"
        sideOffset={8}
        data-testid="workspace-switcher-menu"
      >
        <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-muted-foreground">
          {t('sidebar.switchWorkspace', 'Switch Workspace')}
        </DropdownMenuLabel>

        {workspaces.map((workspace) => {
          const isActive = workspace.accountId === active?.accountId;
          return (
            <DropdownMenuItem
              key={workspace.accountId}
              className="gap-2 px-2 py-2.5"
              disabled={switching !== null}
              onSelect={() => {
                onSelect(workspace);
              }}
            >
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'truncate text-sm font-semibold leading-tight text-sidebar-foreground',
                    switching === workspace.accountId && 'opacity-60',
                  )}
                >
                  {workspace.name ?? noCompany}
                </p>
                <p className="truncate text-xs text-muted-foreground">{t('sidebar.crm', 'CRM')}</p>
              </div>
              {isActive && <Check className="size-4 shrink-0" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
